import { get } from 'lodash-es';

import { isEmptyString } from './string';

export class PageNotFoundError extends Error {
  constructor() {
    super('Page not found');
    this.name = 'PageNotFoundError';
  }
}

/**
 * Extracts a human-readable error message from an unknown error value.
 * Handles route response-like objects, Error instances, strings, and unknown types.
 */
export function getErrorMessage(error: unknown): string {
  if (isRouteErrorResponseLike(error)) {
    const data =
      error.data != null ? (typeof error.data === 'string' ? error.data : JSON.stringify(error.data, null, 2)) : '';
    return `${String(error.status)} ${error.statusText ?? ''}${data !== '' ? `\n${data}` : ''}`;
  }

  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return String(error);
}

function isRouteErrorResponseLike(error: unknown): error is { status: number; statusText?: string; data?: unknown } {
  return typeof error === 'object' && error != null && 'status' in error && typeof error.status === 'number';
}

// Reads `error.response.data` for JSON:API / message / error shapes.
// Returns undefined if the error has no useful body — e.g. network failures,
// timeouts, or aborts. Callers can use that to decide whether to rewrap.
export function getApiErrorBody(error: unknown): string | undefined {
  return (
    asNonEmptyString(get(error, 'response.data.errors[0].detail')) ??
    asNonEmptyString(get(error, 'response.data.message')) ??
    asNonEmptyString(get(error, 'response.data.error'))
  );
}

// Prefers response body, then `.message` (suppressing only axios's useless
// "Request failed with status code N" — network/timeout messages are kept).
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const fromBody = getApiErrorBody(error);
  if (fromBody != null) {
    return fromBody;
  }

  if (error instanceof Error && !isEmptyString(error.message) && !isAxiosStatusMessage(error.message)) {
    return error.message;
  }

  return fallback;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && !isEmptyString(value) ? value : undefined;
}

function isAxiosStatusMessage(message: string): boolean {
  return /^request failed with status code \d+$/i.test(message);
}
